import React from 'react';
import { LogIn, UserPlus, ArrowRight, ShieldCheck, Boxes, BadgePercent, LogOut, Store, CheckCircle } from 'lucide-react';
import { RetailerUser } from '../services/authService';

interface WholesalePageProps {
  retailerUser: RetailerUser | null;
  onLoginClick: () => void;
  onApplyClick: () => void;
  onShopClick: () => void;
  onLogout: () => void;
}

const BENEFITS: { icon: React.ElementType; title: string; body: string }[] = [
  {
    icon: ShieldCheck,
    title: 'The only source in Canada',
    body: 'We are Incolma\'s sole agreed Canadian distributor and wholesaler. Every authentic Incolma blade in the country comes through us — factory-direct from the world\'s largest machete factory, no grey-market stock, no middlemen.',
  },
  {
    icon: BadgePercent,
    title: 'Wholesale pricing',
    body: 'Verified trade pricing across the full Incolma line — unlocked the moment you sign in.',
  },
  {
    icon: Boxes,
    title: 'Mix & match · 12-unit minimum',
    body: 'Build one order across any profiles and sizes. Minimum 12 units total, with no per-style minimums.',
  },
];

const WholesalePage: React.FC<WholesalePageProps> = ({
  retailerUser,
  onLoginClick,
  onApplyClick,
  onShopClick,
  onLogout,
}) => {
  return (
    <div className="bg-paper min-h-screen">
      {/* Hero */}
      <header
        className="relative bg-forge text-paper py-24 lg:py-28 px-6 lg:px-10 overflow-hidden"
        style={{
          backgroundImage: 'url(/images/machete-wall.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center bottom',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, rgba(14,21,24,0.93) 0%, rgba(14,21,24,0.7) 55%, rgba(14,21,24,0.5) 100%)',
          }}
        />
        <div className="relative max-w-editorial mx-auto">
          <p className="font-mono text-[11px] tracking-micro uppercase text-ember-soft mb-6">
            — Wholesale &amp; trade
          </p>
          <h1 className="font-editorial italic font-light text-5xl lg:text-6xl text-paper-warm leading-[1.05] tracking-tight mb-6 max-w-prose">
            Stock the blade the Caribbean trusts.
          </h1>
          <p className="text-paper/70 text-lg max-w-prose leading-[1.6]">
            We are Incolma's exclusive Canadian distributor and wholesaler — trade pricing for
            shops, garden centres, outfitters, and work crews. Sign in to see your wholesale rates,
            or apply for an account — it takes a couple of minutes.
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brass/40 to-transparent" />
      </header>

      <div className="max-w-editorial mx-auto px-6 lg:px-10 py-16 lg:py-20">
        {retailerUser ? (
          /* Signed-in state — wholesale pricing already unlocked */
          <section className="bg-paper-bright border border-ink-200 p-8 lg:p-10 mb-16 lg:mb-20">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 bg-olive/15 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-olive" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-mono text-[10px] tracking-micro uppercase text-olive mb-1.5">
                    — Wholesale pricing unlocked
                  </p>
                  <h2 className="font-editorial italic text-2xl text-ink-950 tracking-tight leading-tight">
                    Welcome back, {retailerUser.businessName}.
                  </h2>
                  <p className="text-sm text-ink-700 mt-2 max-w-prose">
                    Your account is approved. Head to the shop to build an order at your trade pricing.
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-stretch gap-2 md:min-w-[220px]">
                <button
                  onClick={onShopClick}
                  className="inline-flex items-center justify-center gap-2 bg-ember hover:bg-ember-deep text-paper-warm px-6 py-3.5 text-sm font-medium transition-colors duration-150 ease-editorial"
                >
                  <Store className="w-4 h-4" strokeWidth={1.5} />
                  Go to the wholesale shop
                </button>
                <button
                  onClick={onLogout}
                  className="inline-flex items-center justify-center gap-2 text-ink-700 hover:text-ember px-6 py-2 text-sm transition-colors duration-150"
                >
                  <LogOut className="w-4 h-4" strokeWidth={1.5} />
                  Sign out
                </button>
              </div>
            </div>
          </section>
        ) : (
          /* Logged-out — sign in or apply */
          <section className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16 lg:mb-20">
            {/* Sign in */}
            <div className="border border-ink-200 bg-paper-bright p-8 lg:p-10 flex flex-col">
              <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-3">
                — Already approved
              </p>
              <h2 className="font-editorial italic text-3xl text-ink-950 tracking-tight leading-tight mb-3">
                Sign in.
              </h2>
              <p className="text-sm text-ink-700 leading-relaxed mb-8 flex-1">
                Existing wholesale partners — sign in to reveal your trade pricing and place an order.
              </p>
              <button
                onClick={onLoginClick}
                className="inline-flex items-center justify-center gap-2 bg-forge hover:bg-forge-deep text-paper-warm px-6 py-3.5 text-sm font-medium transition-colors duration-150 ease-editorial"
              >
                <LogIn className="w-4 h-4" strokeWidth={1.5} />
                Sign in to your account
              </button>
            </div>

            {/* Apply */}
            <div className="border border-ember bg-ember/5 p-8 lg:p-10 flex flex-col">
              <p className="font-mono text-[10px] tracking-micro uppercase text-ember mb-3">
                — New partner
              </p>
              <h2 className="font-editorial italic text-3xl text-ink-950 tracking-tight leading-tight mb-3">
                Apply for an account.
              </h2>
              <p className="text-sm text-ink-700 leading-relaxed mb-8 flex-1">
                Bring your business number and we'll set you up with wholesale access — usually within
                a business day.
              </p>
              <button
                onClick={onApplyClick}
                className="inline-flex items-center justify-center gap-2 bg-ember hover:bg-ember-deep text-paper-warm px-6 py-3.5 text-sm font-medium transition-colors duration-150 ease-editorial"
              >
                <UserPlus className="w-4 h-4" strokeWidth={1.5} />
                Apply for a wholesale account
              </button>
            </div>
          </section>
        )}

        {/* Benefits */}
        <section>
          <p className="font-mono text-[11px] tracking-micro uppercase text-ember mb-8">
            — Why wholesale with us
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {BENEFITS.map((b) => {
              const Icon = b.icon;
              return (
                <article key={b.title} className="border border-ink-200 bg-paper-bright p-7">
                  <div className="w-10 h-10 bg-ember/10 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-ember" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-editorial italic text-xl text-ink-950 tracking-tight leading-tight mb-2.5">
                    {b.title}.
                  </h3>
                  <p className="text-sm text-ink-700 leading-relaxed">{b.body}</p>
                </article>
              );
            })}
          </div>
        </section>

        {/* Requirements */}
        <div className="mt-12 lg:mt-16 p-8 lg:p-10 bg-forge text-paper">
          <p className="font-mono text-[10px] tracking-micro uppercase text-brass mb-3">
            — What you'll need
          </p>
          <h3 className="font-editorial italic text-2xl text-paper-warm mb-5 tracking-tight">
            Apply in minutes.
          </h3>
          <ul className="space-y-2.5 text-sm text-paper/80 leading-relaxed max-w-prose">
            <li className="flex items-start gap-3">
              <span className="text-ember mt-1 flex-shrink-0">—</span>
              A registered business number (HST / GST or equivalent).
            </li>
            <li className="flex items-start gap-3">
              <span className="text-ember mt-1 flex-shrink-0">—</span>
              A physical storefront or an online presence selling to your customers.
            </li>
            <li className="flex items-start gap-3">
              <span className="text-ember mt-1 flex-shrink-0">—</span>
              A 12-unit minimum order, mixed and matched across the whole catalog.
            </li>
          </ul>
          {!retailerUser && (
            <button
              onClick={onApplyClick}
              className="mt-8 inline-flex items-center gap-2 bg-ember hover:bg-ember-deep text-paper-warm px-6 py-3 text-sm font-medium transition-colors duration-150 ease-editorial"
            >
              Start your application
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WholesalePage;
